// Vendored from the JETCAM DXF Viewer (src/render/spatial.ts), 2026-09-03.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Unmodified. The ../geometry and ../parse imports resolve to the shims
// beside this directory, which re-export the engine.

import RBush from "rbush";
import {
  arcCoversAngle,
  type ArcPrimitive,
  type Bounds,
  boundsOf,
  type PolylinePrimitive,
  type Primitive,
  type TextPrimitive,
} from "../geometry/index.ts";

/**
 * Spatial index over the display list.
 *
 * Two jobs, both per-frame or per-mouse-move: cull everything outside the
 * viewport before drawing, and narrow the hit test to a handful of candidates
 * under the cursor. Both are linear scans over the whole drawing without it,
 * which is what makes a 50,000-primitive nest unusable rather than merely
 * large.
 */

export interface IndexedPrimitive {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  /** Position in the display list; the stable identity of a primitive. */
  readonly id: number;
  readonly primitive: Primitive;
}

/**
 * How wide a character is, as a fraction of cap height.
 *
 * Only used to give text a box for culling and hit-test candidacy. The real
 * width needs font metrics, which are not available where the index is built,
 * so this errs high: an over-large box costs a few needless candidate tests,
 * while an under-large one makes text vanish at the edge of the viewport.
 */
const TEXT_WIDTH_FACTOR = 0.75;

function textRadius(p: TextPrimitive): number {
  const width = p.text.length * p.height * TEXT_WIDTH_FACTOR;
  // Square box about the insertion point, so rotation needs no special case.
  return Math.max(width, p.height);
}

/** Bounding box used for indexing. Generous for text, exact for everything else. */
export function indexBox(p: Primitive): { minX: number; minY: number; maxX: number; maxY: number } {
  if (p.kind === "text") {
    const r = textRadius(p);
    return {
      minX: p.position.x - r,
      minY: p.position.y - r,
      maxX: p.position.x + r,
      maxY: p.position.y + r,
    };
  }
  const b = boundsOf([p]);
  if (!b) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX: b.min.x, minY: b.min.y, maxX: b.max.x, maxY: b.max.y };
}

export class SpatialIndex {
  private readonly tree = new RBush<IndexedPrimitive>();
  readonly entries: IndexedPrimitive[];

  constructor(primitives: readonly Primitive[]) {
    this.entries = primitives.map((primitive, id) => ({ ...indexBox(primitive), id, primitive }));
    // Bulk loading builds a far better balanced tree than repeated inserts,
    // and this index is rebuilt wholesale whenever the display list changes.
    this.tree.load(this.entries);
  }

  /** Everything whose box overlaps the given world rectangle. */
  search(bounds: Bounds): IndexedPrimitive[] {
    return this.tree.search({
      minX: bounds.min.x,
      minY: bounds.min.y,
      maxX: bounds.max.x,
      maxY: bounds.max.y,
    });
  }

  /** Candidates within `tolerance` drawing units of a point. */
  near(x: number, y: number, tolerance: number): IndexedPrimitive[] {
    return this.tree.search({
      minX: x - tolerance,
      minY: y - tolerance,
      maxX: x + tolerance,
      maxY: y + tolerance,
    });
  }
}

// ---------------------------------------------------------------------------
// Precise distance
// ---------------------------------------------------------------------------

function pointSegmentDistance(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const lenSq = abx * abx + aby * aby;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * abx + (py - ay) * aby) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + abx * t), py - (ay + aby * t));
}

function polylineDistance(p: PolylinePrimitive, x: number, y: number): number {
  const pts = p.points;
  if (pts.length === 0) return Infinity;
  if (pts.length === 1) return Math.hypot(x - pts[0]!.x, y - pts[0]!.y);

  let best = Infinity;
  for (let i = 0; i + 1 < pts.length; i += 1) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const d = pointSegmentDistance(x, y, a.x, a.y, b.x, b.y);
    if (d < best) best = d;
  }
  if (p.closed && pts.length > 2) {
    const a = pts[pts.length - 1]!;
    const b = pts[0]!;
    best = Math.min(best, pointSegmentDistance(x, y, a.x, a.y, b.x, b.y));
  }
  return best;
}

function arcDistance(p: ArcPrimitive, x: number, y: number): number {
  const dx = x - p.center.x;
  const dy = y - p.center.y;
  const d = Math.hypot(dx, dy);

  // On the arc's bearing, the nearest point is radially in or out. Off it, the
  // nearest point on the drawn curve is whichever endpoint is closer.
  if (d > 0 && arcCoversAngle(p, Math.atan2(dy, dx))) return Math.abs(d - p.radius);

  const start = {
    x: p.center.x + p.radius * Math.cos(p.startAngle),
    y: p.center.y + p.radius * Math.sin(p.startAngle),
  };
  const end = {
    x: p.center.x + p.radius * Math.cos(p.endAngle),
    y: p.center.y + p.radius * Math.sin(p.endAngle),
  };
  return Math.min(Math.hypot(x - start.x, y - start.y), Math.hypot(x - end.x, y - end.y));
}

/**
 * Distance to a text primitive's box.
 *
 * The point is rotated into the text's own frame and measured against a box
 * running from the insertion point along the baseline. Still approximate —
 * without font metrics it has to be — but far better than treating text as a
 * bare point, which makes a label impossible to hover.
 */
function textDistance(p: TextPrimitive, x: number, y: number): number {
  const cos = Math.cos(-p.rotation);
  const sin = Math.sin(-p.rotation);
  const dx = x - p.position.x;
  const dy = y - p.position.y;
  const lx = dx * cos - dy * sin;
  const ly = dx * sin + dy * cos;

  const width = Math.max(p.height, p.text.length * p.height * TEXT_WIDTH_FACTOR);
  // Roughly the descender-to-cap band around the baseline.
  const bottom = -p.height * 0.25;
  const top = p.height;

  const ox = lx < 0 ? -lx : lx > width ? lx - width : 0;
  const oy = ly < bottom ? bottom - ly : ly > top ? ly - top : 0;
  return Math.hypot(ox, oy);
}

/** Shortest distance from a world point to a primitive, in drawing units. */
export function distanceToPrimitive(p: Primitive, x: number, y: number): number {
  switch (p.kind) {
    case "line":
      return pointSegmentDistance(x, y, p.a.x, p.a.y, p.b.x, p.b.y);
    case "polyline":
      return polylineDistance(p, x, y);
    case "arc":
      return arcDistance(p, x, y);
    case "point":
      return Math.hypot(x - p.position.x, y - p.position.y);
    case "text":
      return textDistance(p, x, y);
  }
}

export interface Hit {
  readonly entry: IndexedPrimitive;
  /** Distance in drawing units. */
  readonly distance: number;
}

/**
 * The nearest primitive within `tolerance` drawing units, or null.
 *
 * The index narrows the field by bounding box; this then measures the real
 * geometry, so a click inside the bounding box of a large arc but nowhere near
 * the arc itself correctly misses.
 */
export function hitTest(
  index: SpatialIndex,
  x: number,
  y: number,
  tolerance: number,
  accept?: (entry: IndexedPrimitive) => boolean,
): Hit | null {
  let best: Hit | null = null;
  for (const entry of index.near(x, y, tolerance)) {
    // Filtering here rather than on the result: rejecting afterwards would
    // return nothing when the nearest thing happens to be unpickable, instead
    // of the next-nearest thing that is.
    if (accept && !accept(entry)) continue;
    const distance = distanceToPrimitive(entry.primitive, x, y);
    if (distance > tolerance) continue;
    if (!best || distance < best.distance) best = { entry, distance };
  }
  return best;
}
